import { inject, Injectable, InjectionToken } from '@angular/core';
import { AuthenticationResult } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserRole, UserRoles } from '@core/constants';
import { AccessToken } from '../models/access-token.model';
import { DecodedToken } from '../models/decoded.token.interface';
import { User } from '../models/user.model';
import { AccessTokenStorageService } from './access-token-storage.service';
import { IUserService } from './user.service.interface';

export const JWT_DECODE = new InjectionToken<typeof jwtDecode>('jwt.decode', {
    providedIn: 'root',
    factory: () => jwtDecode
});

@Injectable({
    providedIn: 'root'
})
export class UserService implements IUserService {
    protected currentUserSubject = new BehaviorSubject<User | null>(null);
    public currentUser: Observable<User | null> = this.currentUserSubject.asObservable();

    private readonly accessTokenService = inject(AccessTokenStorageService);
    private readonly jwtDecode = inject(JWT_DECODE);
    private readonly userRoleSet = new Set<UserRole>(Object.values(UserRoles));

    public get currentUserValue(): User | null {
        return this.currentUserSubject.value;
    }

    public createUserFromAuthenticationResult(authResult: AuthenticationResult): User {
        const decodedToken: DecodedToken = this.jwtDecode(authResult.accessToken);
        const roles = this.getUserRoles(decodedToken.roles);
        const expiresOn = authResult.expiresOn ?? new Date(0);

        const accessToken = new AccessToken(
            authResult.account.username,
            roles,
            expiresOn,
            decodedToken.scp?.split(' ') || []
        );

        const isAdmin = this.hasAdminRole(roles);

        const user = new User(authResult.account.name ?? '', authResult.account.username, isAdmin, accessToken);

        this.storeUser(user);
        this.accessTokenService.storeToken(authResult.accessToken, expiresOn);
        return user;
    }

    public isUserAdmin(): boolean {
        return this.currentUserValue?.isAdmin ?? false;
    }

    private storeUser(user: User): void {
        this.currentUserSubject.next(user);
    }

    public clearUser(): void {
        this.currentUserSubject.next(null);
        this.accessTokenService.clearToken();
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public setAdmin(): void {}

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public setUser(): void {}

    protected hasAdminRole(roles: readonly UserRole[]): boolean {
        return roles.includes(UserRoles.Admin) || roles.includes(UserRoles.KeyUser);
    }

    private getUserRoles(roles: readonly string[] | undefined): UserRole[] {
        return roles?.filter(role => this.isUserRole(role)) ?? [];
    }

    private isUserRole(role: string): role is UserRole {
        return this.userRoleSet.has(role as UserRole);
    }
}
